import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { FaArrowRightLong, FaBoxesStacked, FaClockRotateLeft, FaCloudArrowUp, FaShieldHalved } from "react-icons/fa6";
import { BellRing, CheckCircle2, ListChecks, LoaderCircle, PackagePlus, Sparkles, TrendingUp, Workflow } from "lucide-react";
import { BASE_URL_API } from "../../References/URLs";
import ActiveStoreContext from "../../store/ActiveStoreContext";
import AuthContext from "../../store/auth-context";
import styles from "./ImLanding.module.css";

const ImLanding = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = JSON.parse(authCtx?.loginData || "{}")?.partnersData?.vendorIdentifier;

    const [hasAddedProduct, setHasAddedProduct] = useState(false);
    const [isCheckingInventory, setIsCheckingInventory] = useState(true);

    const gettingStartedSteps = useMemo(() => ([
        {
            title: "Create Product Master",
            desc: "Upload or create your master list once. Every stock action will use this list.",
            link: "/manage/inventory/add-products",
            cta: "Create Master List",
            icon: PackagePlus,
        },
        {
            title: "Review Product List",
            desc: "Open your catalog, verify names/prices, and keep product data clean.",
            link: "/manage/inventory/products",
            cta: "View Product List",
            icon: ListChecks,
        },
        {
            title: "Stock Up in Seconds",
            desc: "Add stock instantly. Deductions, alerts, and reminders are auto-managed.",
            link: "/manage/inventory/add-stock",
            cta: "Manage Stock",
            icon: FaBoxesStacked,
        },
    ]), []);

    const activeInventoryActions = useMemo(() => ([
        {
            title: "Add New Products",
            desc: "Expand your catalog anytime with single or bulk uploads.",
            link: "/manage/inventory/add-products",
            cta: "Add Products",
            icon: FaCloudArrowUp,
        },
        {
            title: "Open Product Catalog",
            desc: "Review, search, and maintain all products from one place.",
            link: "/manage/inventory/products",
            cta: "Go To Catalog",
            icon: Workflow,
        },
        {
            title: "Update Stock Levels",
            desc: "Stock in, stock corrections, and tracking happen in one clean flow.",
            link: "/manage/inventory/add-stock",
            cta: "Update Stock",
            icon: TrendingUp,
        },
    ]), []);

    const firstTimeBenefits = useMemo(() => ([
        "No technical setup needed — your team can start in minutes.",
        "One product master powers billing, stock updates, and future expansion.",
        "Avoid mismatch issues with cleaner product and pricing control.",
        "Automatic stock deductions and alerts reduce manual follow-ups.",
    ]), []);

    const activeBenefits = useMemo(() => ([
        "Run daily stock operations faster with one-click action routes.",
        "Catch low stock before it hurts sales with proactive reminders.",
        "Keep inventory decisions data-driven with organized product control.",
        "Scale your catalog confidently without losing operational accuracy.",
    ]), []);

    const whyBestCards = useMemo(() => ([
        {
            title: "Built for real teams",
            desc: "Designed for day-to-day store operations where speed and accuracy both matter.",
        },
        {
            title: "Automation-first workflow",
            desc: "Deductions, alerts, and stock nudges are automated so your team focuses on growth.",
        },
        {
            title: "Consistent product lifecycle",
            desc: "Create, list, stock, monitor — every stage is connected in one inventory ecosystem.",
        },
    ]), []);

    useEffect(() => {
        const getInventoryProducts = async () => {
            if (!activeStore?.storeIdentifier || !vendorIdentifier || !authCtx?.token) {
                setHasAddedProduct(false);
                setIsCheckingInventory(false);
                return;
            }

            try {
                setIsCheckingInventory(true);
                const response = await fetch(BASE_URL_API + "getInventoryProducts", {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx?.token,
                        vendorIdentifier,
                        storeIdentifier: activeStore?.storeIdentifier,
                    }),
                });

                if (!response.ok) {
                    setHasAddedProduct(false);
                    return;
                }

                const respo = await response.json();
                if (respo?.status === "success") {
                    setHasAddedProduct(Array.isArray(respo?.response) && respo.response.length > 0);
                } else {
                    if (respo?.message === "tokenExpired") {
                        authCtx.logout();
                    }
                    setHasAddedProduct(false);
                }
            } catch (error) {
                console.log("Unable to check inventory status", error);
                setHasAddedProduct(false);
            } finally {
                setIsCheckingInventory(false);
                
            }
        };

        getInventoryProducts();
    }, [activeStore?.storeIdentifier, vendorIdentifier, authCtx?.token]);

    const viewState = isCheckingInventory ? "loading" : (hasAddedProduct ? "active" : "onboarding");

    const heroTitle = viewState === "loading"
        ? "Preparing Your Inventory Experience"
        : hasAddedProduct
        ? "Your Inventory Engine Is Running Smoothly"
        : "Build Your Inventory Foundation in 3 Easy Steps";

    const heroSubtitle = viewState === "loading"
        ? "We’re checking your catalog status to show the right guidance for your inventory journey."
        : hasAddedProduct
        ? "Great work — your product catalog is active. Keep stock fresh, track movement, and stay ahead with automatic deductions, alerts, and reminders."
        : "Start once, run forever: create your product master, review your list, and stock-up. After that, the system handles deductions, low-stock alerts, and reminders automatically.";

    const pulseItems = [
        { title: "Auto Deductions", icon: Sparkles },
        { title: "Low-Stock Alerts", icon: BellRing },
        { title: "Smart Reminders", icon: FaClockRotateLeft },
        { title: "Safe Control", icon: FaShieldHalved },
    ];

    const heroPrimaryCta = viewState === "loading"
        ? { label: "Launch Product Setup", to: "/manage/inventory/add-products", icon: PackagePlus }
        : viewState === "active"
            ? { label: "Optimize Inventory Now", to: "/manage/inventory/add-stock", icon: TrendingUp }
            : { label: "Start With Product Master", to: "/manage/inventory/add-products", icon: PackagePlus };

    const heroSecondaryCta = viewState === "loading"
        ? { label: "Open Product Catalog", to: "/manage/inventory/products", icon: Workflow }
        : viewState === "active"
            ? { label: "Add More Products", to: "/manage/inventory/add-products", icon: FaCloudArrowUp }
            : { label: "View Product List", to: "/manage/inventory/products", icon: ListChecks };

    const PrimaryCtaIcon = heroPrimaryCta.icon;
    const SecondaryCtaIcon = heroSecondaryCta.icon;

    return (
        <div className={styles.landing}>
            <motion.section
                className={styles.hero}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
            >
                <div className={styles.badgesRow}>
                    <span className={styles.statusPill}>
                        {viewState === "loading"
                            ? "Checking inventory status..."
                            : hasAddedProduct
                                ? "Inventory Active"
                                : "First-Time Setup"}
                    </span>
                    <span className={styles.sparkleTag}>
                        <Sparkles size={16} /> Smart Inventory Flow
                    </span>
                </div>
                <h1>{heroTitle}</h1>
                <p>{heroSubtitle}</p>

                <div className={styles.heroActions}>
                    <Link to={heroPrimaryCta.to} className={styles.primaryBtn}>
                        <PrimaryCtaIcon size={17} />
                        {heroPrimaryCta.label}
                    </Link>
                    <Link to={heroSecondaryCta.to} className={styles.secondaryBtn}>
                        <SecondaryCtaIcon size={15} />
                        {heroSecondaryCta.label}
                    </Link>
                </div>

                <div className={styles.pulseStrip}>
                    {pulseItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <motion.div
                                className={styles.pulseItem}
                                key={item.title}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: index * 0.08 }}
                            >
                                <Icon size={16} />
                                <span>{item.title}</span>
                            </motion.div>
                        );
                    })}
                </div>
            </motion.section>

            {viewState === "loading" ? (
                <section className={styles.loadingSection}>
                    <motion.div
                        className={styles.loadingCard}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        <LoaderCircle size={18} className={styles.loadingIcon} />
                        <h3>Analyzing inventory profile...</h3>
                        <p>
                            We’ll show either the guided first-time setup or your advanced quick-action dashboard.
                        </p>
                    </motion.div>
                </section>
            ) : viewState === "onboarding" ? (
                <section className={styles.flowSection}>
                    <div className={styles.sectionHeader}>
                        <h2>Simple setup. Powerful automation.</h2>
                        <p>
                            One guided flow to get inventory running with no confusion.
                        </p>
                    </div>

                    <div className={styles.stepGrid}>
                        {gettingStartedSteps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <motion.div
                                    key={step.title}
                                    className={styles.stepCard}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.35, delay: index * 0.1 }}
                                >
                                    <div className={styles.stepTopRow}>
                                        <div className={styles.stepBadge}>Step {index + 1}</div>
                                        <Icon size={18} />
                                    </div>
                                    <h3>{step.title}</h3>
                                    <p>{step.desc}</p>
                                    <Link to={step.link} className={styles.inlineAction}>
                                        {step.cta} <FaArrowRightLong size={12} />
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className={styles.contentBand}>
                        <div className={styles.contentBandHeader}>
                            <h3>Why this is the best way to start inventory</h3>
                            <p>
                                You don’t need to build processes manually — the system gives you a ready workflow that grows with your business.
                            </p>
                        </div>
                        <div className={styles.bulletGrid}>
                            {firstTimeBenefits.map((item) => (
                                <div key={item} className={styles.bulletCard}>
                                    <CheckCircle2 size={16} />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ) : (
                <section className={styles.flowSection}>
                    <div className={styles.sectionHeader}>
                        <h2>Keep momentum with quick actions</h2>
                        <p>
                            Your catalog already exists — now optimize stock movement and stay ahead.
                        </p>
                    </div>

                    <div className={styles.stepGrid}>
                        {activeInventoryActions.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <motion.div
                                    key={step.title}
                                    className={styles.stepCard}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.35, delay: index * 0.1 }}
                                >
                                    <div className={styles.stepTopRow}>
                                        <div className={styles.stepBadge}>Quick Action</div>
                                        <Icon size={18} />
                                    </div>
                                    <h3>{step.title}</h3>
                                    <p>{step.desc}</p>
                                    <Link to={step.link} className={styles.inlineAction}>
                                        {step.cta} <FaArrowRightLong size={12} />
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className={styles.contentBand}>
                        <div className={styles.contentBandHeader}>
                            <h3>Why teams keep relying on this inventory system</h3>
                            <p>
                                It keeps the operation stable: fewer manual misses, faster stock decisions, and cleaner product control every day.
                            </p>
                        </div>
                        <div className={styles.bulletGrid}>
                            {activeBenefits.map((item) => (
                                <div key={item} className={styles.bulletCard}>
                                    <CheckCircle2 size={16} />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className={styles.bestSection}>
                <div className={styles.sectionHeader}>
                    <h2>What makes this platform stand out</h2>
                    <p>It is not just stock entry — it is an operational advantage.</p>
                </div>
                <div className={styles.bestGrid}>
                    {whyBestCards.map((card, index) => (
                        <motion.div
                            key={card.title}
                            className={styles.bestCard}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.3, delay: index * 0.08 }}
                        >
                            <h4>{card.title}</h4>
                            <p>{card.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <motion.section
                className={styles.trustSection}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4 }}
            >
                <div className={styles.trustCard}>
                    <FaShieldHalved size={18} />
                    <div>
                        <h4>Safe by default</h4>
                        <p>Every update is structured to reduce manual errors and improve stock reliability.</p>
                    </div>
                </div>
                <div className={styles.trustCard}>
                    <BellRing size={18} />
                    <div>
                        <h4>Always informed</h4>
                        <p>Track stock events with alerts and reminders so nothing critical is missed.</p>
                    </div>
                </div>
                <div className={styles.trustCard}>
                    <FaClockRotateLeft size={18} />
                    <div>
                        <h4>Fast daily operations</h4>
                        <p>From setup to stock updates, the whole flow is built for speed and clarity.</p>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default ImLanding;
